import React, { useCallback, useEffect, useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Menu, X, Truck, ArrowRight } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { Logo, LOGOS_HIDDEN } from './Logo';
import { useCart } from './CartContext';
import { motion, AnimatePresence } from 'framer-motion';
import { categories } from '../data/products';
import { ChevronDown } from 'lucide-react';
import { useT } from '../utils/i18n';
interface NavbarProps {
  isDark: boolean;
  toggleDark: () => void;
}
export function Navbar({
  isDark,
  toggleDark
}: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const [mobileHelpOpen, setMobileHelpOpen] = useState(false);
  const [logoRipple, setLogoRipple] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [navRetracted, setNavRetracted] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const lastScrollY = useRef(0);
  // Reset mobile dropdowns when menu closes
  useEffect(() => {
    if (!isOpen) {
      setMobileShopOpen(false);
      setMobileHelpOpen(false);
    }
  }, [isOpen]);
  useEffect(() => {
    const onResize = () => setIsMobileNav(window.innerWidth < 768);
    window.addEventListener('resize', onResize, {
      passive: true
    });
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 50);
      // Mobile retract: slide up 50% when scrolling down, restore at top
      const isMobile = window.innerWidth < 1300;
      if (!isMobile) {
        setNavRetracted(false);
      } else if (y === 0) {
        setNavRetracted(false);
      } else if (y > lastScrollY.current && y > 60) {
        setNavRetracted(true);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const isContractorPage = location.pathname.includes('subcontractors') || location.pathname.includes('contractor');
  const navLinkClass = 'text-stone-800 dark:text-white';
  const dropdownLinkClass = 'text-stone-600 dark:text-stone-300';
  // Category labels in the Shop Materials menu use the brand green in
  // BOTH light and dark modes — matches the visual treatment customers
  // already see for the green "LIVE", "Pay on delivery", and pill chips
  // throughout the site, so the Shop section reads as on-brand instead
  // of a muted secondary palette. Contractor pages override to
  // brand-orange to preserve the contractor-specific visual identity.
  const categoryLabelClass = isContractorPage ? 'text-brand-orange' : 'text-[#17A347] dark:text-[#22c95c]';
  const {
    totalItems,
    totalPrice,
    toggleCart
  } = useCart();
  const t = useT();
  // Helper to handle anchor links from other pages
  const getAnchorHref = (hash: string) => {
    if (location.pathname !== '/') {
      return `/${hash}`;
    }
    return hash;
  };
  const goToQuote = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      navigate('/');
    }
  }, [location.pathname, navigate]);
  const collapseCard = useCallback(() => {
    window.dispatchEvent(new Event('materialsCollapse'));
  }, []);
  const resetMaterials = useCallback(() => {
    collapseCard();
    // Also reset active tab to materials
    window.dispatchEvent(new Event('materialsReset'));
  }, [collapseCard]);
  // Navigate straight to a product's detail page from the Shop dropdown.
  // Previously this routed everything through the home-page Materials Hub
  // (/?cat=…&prod=…#materials), which meant clicking a specific product
  // from any non-home page dumped the customer at the hub instead of the
  // product they actually wanted. Now we go directly to /shop/:slug.
  const goToProduct = useCallback((slug: string) => {
    navigate(`/shop/${slug}`);
  }, [navigate]);
  return <nav className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? 'bg-[#ffffff] dark:bg-black backdrop-blur-md shadow-lg' : 'bg-[#ffffff] dark:bg-black/75 backdrop-blur-md'}`} style={{
    overflow: navRetracted && !isOpen ? 'hidden' : 'visible',
    height: navRetracted && !isOpen ? isMobileNav ? '64px' : '90px' : isMobileNav ? '80px' : '113px',
    transition: 'height 0.3s ease'
  }} data-id="element-1295" data-mp="6o7kdvrdiftf">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1296" data-mp="pmjwnqdl05t8">
        <div className="flex justify-between items-center" style={{
        height: navRetracted && !isOpen ? isMobileNav ? '64px' : '90px' : isMobileNav ? '80px' : '113px',
        transition: 'height 0.3s ease'
      }} data-id="element-1297" data-mp="pkz60bue170n">
          <div className="flex-shrink-0 flex items-center gap-4" data-id="element-1298" data-mp="2h9qf7jmbplvx">
            <Link to="/" onClick={resetMaterials} className="relative" aria-label="206Gravel home" data-id="element-1299" data-mp="2hcky36pouyn8">
              <motion.div className="origin-left" initial={{
              opacity: 0,
              scale: 1
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              opacity: {
                duration: 0.4,
                ease: 'easeOut'
              }
            }} data-id="element-1300" data-mp="2m6ymuq9ui7ix">
                {!LOGOS_HIDDEN && (
              /* Region-pinned wordmark: shows "253GRAVEL" / "206GRAVEL" /
              "425GRAVEL" based on the visitor's domain. No rotation —
              the navbar reads as a fixed regional identity. */
              /* Full wordmark at the top of the page, green "206" glyph once
              scrolled. Keyed off `isScrolled` rather than `navRetracted`,
              since retraction is disabled above 1300px and desktop would
              otherwise never swap. */
              /* Sized to the largest the bar allows: the retracted heights (64px
              mobile / 90px desktop) are the binding constraint, so h-12 / h-16
              fills them while leaving ~8px of breathing room. */
              <Logo pinned imgClassName="h-[55px] max-sm:h-[35px] w-auto" invertOnLight tinted={isScrolled && !isOpen} data-id="element-1301" data-mp="1xknq7ip4fd70" />)}
              </motion.div>
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8" data-id="element-1302" data-mp="27j3j9r7pkwj5">
            {/* Shop Dropdown */}
            <div className="relative" onMouseEnter={() => setIsShopOpen(true)} onMouseLeave={() => setIsShopOpen(false)} data-id="element-1303" data-mp="22mmssqd1e62w">
              <button className={cn(`flex items-center ${navLinkClass} hover:text-brand-orange font-bold text-sm tracking-wide uppercase transition-colors py-2`, 'font-[600]')} data-id="element-1304" data-mp="d6ncmpb03gdr">
                {t('nav.shop')}
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-1305" data-mp="2jx068o9ok580" />
              </button>

              <AnimatePresence data-id="element-1306" data-mp="2osz1mvtfenhx">
                {isShopOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full left-0 bg-[#fcfcfc] dark:bg-black/85 backdrop-blur-md border border-stone-200 dark:border-white/20 rounded-xl shadow-xl p-5 z-50" style={{
                width: '620px'
              }} data-id="element-1307" data-mp="2a5dzb44dksb2">
                    <div className="grid grid-cols-3 gap-5" data-id="element-1308" data-mp="1w5lchjvyyw8d">
                      {categories.map(cat => <div key={cat.name} data-id="element-1309" data-mp="3ll2nfytd7xdg">
                          <p className={`text-xs font-bold ${categoryLabelClass} uppercase tracking-wider mb-2`} data-id="element-1310" data-mp="3li6oq89p14ny">
                            {cat.name}
                          </p>
                          <div className="space-y-1.5" data-id="element-1311" data-mp="2e8xucbbohusz">
                            {cat.products.map(prod => <button key={prod.slug} onClick={() => {
                        setIsShopOpen(false);
                        goToProduct(prod.slug);
                      }} className={`block text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors text-left w-full px-2 py-1.5 -mx-2 rounded-md`} data-id="element-1312" data-mp="29b65xra8sqtj">
                                {prod.name}
                              </button>)}
                          </div>
                        </div>)}
                    </div>

                    <div className="mt-6 border-t border-stone-200 pt-4 dark:border-white/10" data-id="element-1313" data-mp="1gms5z5zlfkfc">
                      <Link to="/projects/new-gravel-driveway" onClick={() => {
                    setIsShopOpen(false);
                    collapseCard();
                  }} className="group flex items-center justify-between bg-[#d8ecdb] px-4 py-3 text-[#04A05C] transition-colors hover:bg-[#c8e5ce] dark:bg-[#04A05C] dark:text-white dark:hover:bg-[#05894e]" data-id="element-1314" data-mp="3fzmfzcmyjmcf">
                        <div data-id="element-1315" data-mp="3ng9bkohzc9at">
                          <span className="block text-sm font-bold text-[#04A05C] dark:text-white" data-id="element-1316" data-mp="3s8spjmkkx6g5">
                            Shop by Project
                          </span>
                          <span className="mt-0.5 block text-xs text-[#05894e] dark:text-white/80" data-id="element-1317" data-mp="2zkoh4tamif9r">
                            Driveway, patio, drainage & more
                          </span>
                        </div>
                        <ArrowRight className="h-4 w-4 text-[#04A05C] transition-transform group-hover:translate-x-1 dark:text-white" data-id="element-1318" data-mp="39ib6k9pjtp7z" />
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Resources Dropdown. Holds the Calculator & Quiz, Compare
             Materials, DIY Guide, Delivery Prep Guide, and Contractors
             Zone links — most of which are reference content, not
             decision tools. Labelled "Resources" so it matches the
             actual contents (and matches the mobile section header). */}
            <div className="relative" onMouseEnter={() => setIsHelpOpen(true)} onMouseLeave={() => setIsHelpOpen(false)} data-id="element-1319" data-mp="lqrrumnv13y4">
              <button className={cn(`flex items-center ${navLinkClass} hover:text-brand-orange font-bold text-sm tracking-wide uppercase transition-colors py-2`, 'font-[600]')} data-id="element-1320" data-mp="qnrxiwgss4op">
                {t('nav.resources')}
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-1321" data-mp="3u73dknmu03z8" />
              </button>
              <AnimatePresence data-id="element-1322" data-mp="2xnv2sfciaza">
                {isHelpOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute left-0 mt-2 w-56 bg-[#fcfcfc] dark:bg-black/85 backdrop-blur-md rounded-xl shadow-xl border border-stone-200 dark:border-white/20 overflow-hidden" data-id="element-1323" data-mp="7ylc31omcyz7">
                    <div className="py-2" data-id="element-1324" data-mp="2u56ojui3ligh">
                      <Link to="/help" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-1325" data-mp="2u459zg1vidu0">
                        Calculator & Quiz
                      </Link>
                      <Link to="/compare" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-1326" data-mp="2g4niuu13uohh">
                        {t('nav.compareMaterials')}
                      </Link>
                      <Link to="/projects/new-gravel-driveway" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-1327" data-mp="9fh7rbxu3ml5">
                        Project Guides
                      </Link>
                      <Link to="/delivery-prep" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-1328" data-mp="23o4l7ogy4k66">
                        Delivery Prep Guide
                      </Link>
                      <Link to="/blog" className={`block px-4 py-2 text-sm ${dropdownLinkClass} hover:bg-[#f0f0f0] dark:hover:bg-[#2a2a2a] hover:text-brand-orange dark:hover:text-brand-orange transition-colors`} data-id="element-1329" data-mp="2hnvwhhg2vmgh">
                        Blog
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* About — a single link now that "Why shop local" is part of that
                same page, so a dropdown holding one destination is gone. */}
            <Link to="/about" onClick={collapseCard} className={`${navLinkClass} hover:text-brand-orange font-medium transition-colors`} data-id="element-1330" data-mp="119xecfh3nmu2">
              {t('nav.about')}
            </Link>
            <Link to="/contact" onClick={collapseCard} className={`${navLinkClass} hover:text-brand-orange font-medium transition-colors`} data-id="element-1331" data-mp="opgugff2gaun">
              Contact
            </Link>

            <div className="hidden md:flex items-center space-x-6" data-id="element-1332" data-mp="yp7ranr3n1xw">
              <div className="flex items-center space-x-3" data-id="element-1333" data-mp="ayc0ntydqcs8">
                <Link to="/delivery-zones" onClick={collapseCard} aria-label="Delivery Zones" title="Delivery Zones" className="relative w-9 h-9 rounded-full flex items-center justify-center text-stone-800 dark:text-white hover:text-brand-orange transition-colors group" data-id="element-1334" data-mp="ayvcqu1cm6yc">
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-1335" data-mp="fwwsp63h9q5l">
                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="#5c5c5c" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1336" data-mp="3ocgh9rt7ekeb">
                      <circle cx="12" cy="12" r="10" fill="none" data-id="element-1337" data-mp="2mntf2oq283yz" />
                    </svg>
                  </div>
                  <motion.div className="absolute inset-1 pointer-events-none" animate={{
                  rotate: 360
                }} transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: 'linear'
                }} style={{
                  background: isDark ? 'conic-gradient(from 0deg, rgba(255,255,255,0.5) 0deg, rgba(255,255,255,0) 90deg, transparent 360deg)' : 'conic-gradient(from 0deg, rgba(41,37,36,0.5) 0deg, rgba(41,37,36,0) 90deg, transparent 360deg)',
                  WebkitMaskImage: 'radial-gradient(circle, black 70%, transparent 71%)',
                  maskImage: 'radial-gradient(circle, black 70%, transparent 71%)',
                  borderRadius: '9999px'
                }} data-id="element-1338" data-mp="2qpey6rt6tehm" />
                </Link>
                <ThemeToggle isDark={isDark} onToggle={toggleDark} data-id="element-1339" data-mp="1oygklxdaz5pu" />
              </div>
              <div className="h-6 w-px bg-stone-200 dark:bg-white/20" data-id="element-1340" data-mp="1hg8xz5v6dhyc" />
              {/* Desktop Cart Button */}
              <button onClick={toggleCart} className={`relative flex items-center gap-2 transition-all ${totalItems > 0 ? 'pl-2.5 pr-3.5 py-1.5 rounded-full bg-stone-200/80 dark:bg-white/10 hover:bg-stone-300/80 dark:hover:bg-white/15 text-stone-900 dark:text-white' : 'p-2 text-stone-800 dark:text-white hover:text-brand-orange'}`} aria-label={totalItems > 0 ? `Open cart with ${totalItems} items, subtotal ${totalPrice.toLocaleString()}` : 'Open cart'} data-id="element-1341" data-mp="1cnzb5ypcwy0a">
                <div className="relative" data-id="element-1342" data-mp="1vdp1a8i6cvlv">
                  <Truck className="w-5 h-5" data-id="element-1343" data-mp="1qfduy7r3938e" />
                  <AnimatePresence data-id="element-1344" data-mp="1qi9t9x6ultxd">
                    {totalItems > 0 && <motion.span initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} exit={{
                    scale: 0
                  }} className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 bg-brand-orange text-white text-[10px] font-bold rounded-full" data-id="element-1345" data-mp="1mdsd07zp5yrc">
                        {totalItems}
                      </motion.span>}
                  </AnimatePresence>
                </div>
                <AnimatePresence data-id="element-1346" data-mp="2huvnfyoz7qe">
                  {totalItems > 0 && <motion.span initial={{
                  opacity: 0,
                  width: 0
                }} animate={{
                  opacity: 1,
                  width: 'auto'
                }} exit={{
                  opacity: 0,
                  width: 0
                }} className="text-sm font-bold whitespace-nowrap overflow-hidden" data-id="element-1347" data-mp="7hr01gbax16h">
                      ${totalPrice.toLocaleString()}
                    </motion.span>}
                </AnimatePresence>
              </button>
            </div>
          </div>

          <div className="md:hidden flex items-center space-x-3" data-id="element-1348" data-mp="36xxdz61630sw">
            <ThemeToggle isDark={isDark} onToggle={toggleDark} data-id="element-1349" data-mp="3ay7w1h28hho4" />
            {/* Mobile Cart Button */}
            <button onClick={toggleCart} className={`relative flex items-center gap-1.5 transition-all ${totalItems > 0 ? 'pl-2 pr-2.5 py-1.5 rounded-full bg-stone-200/80 dark:bg-white/10 text-stone-900 dark:text-white' : 'p-2 text-stone-800 dark:text-white hover:text-brand-orange'}`} aria-label={totalItems > 0 ? `Open cart with ${totalItems} items, subtotal ${totalPrice.toLocaleString()}` : 'Open cart'} data-id="element-1350" data-mp="2s9vyrc0aak2h">
              <div className="relative" data-id="element-1351" data-mp="3b8hzgprw295v">
                <Truck className="w-5 h-5" data-id="element-1352" data-mp="3b7gmgfrbbcli" />
                <AnimatePresence data-id="element-1353" data-mp="36a6q0g5ia0o6">
                  {totalItems > 0 && <motion.span initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} exit={{
                  scale: 0
                }} className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 bg-brand-orange text-white text-[10px] font-bold rounded-full" data-id="element-1354" data-mp="369omqzkfem6r">
                      {totalItems}
                    </motion.span>}
                </AnimatePresence>
              </div>
              {totalItems > 0 && <span className="text-xs font-bold whitespace-nowrap" data-id="element-1355" data-mp="1077ze5u1yyij">
                  ${totalPrice.toLocaleString()}
                </span>}
            </button>

            <button onClick={() => setIsOpen(!isOpen)} className="text-stone-800 dark:text-white hover:text-brand-orange focus:outline-none p-2" aria-label={isOpen ? 'Close menu' : 'Open menu'} data-id="element-1356" data-mp="197wfr76x5ayl">
              {isOpen ? <X className="h-6 w-6" data-id="element-1357" data-mp="gkjr8rh76t2n" /> : <Menu className="h-6 w-6" data-id="element-1358" data-mp="gl3782naf7e1" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-1359" data-mp="liv1v4fbh7lx">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} onClick={() => setIsOpen(false)} className="md:hidden fixed inset-0 bg-black/40 z-[9998]" data-id="element-1360" data-mp="qgwkjo8tz4wv" />
            {/* Pull-down menu */}
            <motion.div initial={{
          y: '-100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '-100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="md:hidden fixed top-0 left-0 right-0 bg-[#fcfcfc] dark:bg-black/85 backdrop-blur-md shadow-2xl z-[9999] overflow-y-auto max-h-screen" data-id="element-1361" data-mp="2q0w913l2rud">
              <div className="flex justify-end items-center p-6" data-id="element-1362" data-mp="3cgg9awyctz32">
                <button onClick={() => setIsOpen(false)} className="text-stone-800 dark:text-white hover:text-brand-orange p-2" aria-label="Close menu" data-id="element-1363" data-mp="38g63pleh5aoq">
                  <X className="w-6 h-6" data-id="element-1364" data-mp="33mjnxkd0kqop" />
                </button>
              </div>
              <div className="px-8 pb-10 space-y-7 flex flex-col text-right font-nav" data-id="element-1365" data-mp="2yn73f69m7vpa">
                {/* Mobile Shop Menu - Collapsible */}
                <div className="py-2 border-b border-stone-200 dark:border-[#2a2a2a]" data-id="element-1366" data-mp="2orn7wkyyqz12">
                  <button onClick={() => setMobileShopOpen(!mobileShopOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white font-bold gap-2 text-lg" data-id="element-1367" data-mp="2kpsbnuvw3bu5">
                    Shop Materials
                    <motion.div animate={{
                  rotate: mobileShopOpen ? 180 : 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-1368" data-mp="14mr2uem2eeca">
                      <ChevronDown className="w-5 h-5" data-id="element-1369" data-mp="vmkpqtua3gdn" />
                    </motion.div>
                  </button>
                  <AnimatePresence data-id="element-1370" data-mp="qnhkp9eata5o">
                    {mobileShopOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-1371" data-mp="qpaqkmh37qcm">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-5 pt-4 pr-1" data-id="element-1372" data-mp="7yt1qszdlewj">
                          {categories.map(cat => <div key={cat.name} data-id="element-1373" data-mp="2zzrd9fdy40l">
                              <p className={`text-xs font-bold ${categoryLabelClass} uppercase tracking-wider mb-2 text-right`} data-id="element-1374" data-mp="2ejxkj4vb3uw5">
                                {cat.name}
                              </p>
                              <div className="space-y-3" data-id="element-1375" data-mp="2okhmk9qnh3ff">
                                {cat.products.map(prod => <button key={prod.slug} onClick={() => {
                          setIsOpen(false);
                          goToProduct(prod.slug);
                        }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right w-full py-1.5" data-id="element-1376" data-mp="2sjqh3gtil5c1">
                                    {prod.name}
                                  </button>)}
                              </div>
                            </div>)}
                        </div>

                        <div className="mt-4 space-y-2 border-t border-stone-200 pt-3 dark:border-white/10" data-id="element-1377" data-mp="1d3867w2tiric">
                          <Link to="/projects/new-gravel-driveway" onClick={() => {
                      setIsOpen(false);
                      collapseCard();
                    }} className="flex items-center justify-between bg-[#d8ecdb] px-4 py-3 text-[#04A05C] transition-colors hover:bg-[#c8e5ce] dark:bg-[#04A05C] dark:text-white dark:hover:bg-[#05894e]" data-id="element-1378" data-mp="1h41qmfh1xo6h">
                            <div className="flex-1 text-right" data-id="element-1379" data-mp="2p6z1g5z0wgya">
                              <span className="block text-sm font-bold text-[#04A05C] dark:text-white" data-id="element-1380" data-mp="21hmtf9ffmexk">
                                Shop by Project
                              </span>
                              <span className="mt-0.5 block text-xs text-[#05894e] dark:text-white/80" data-id="element-1381" data-mp="1wkweipfd5pdq">
                                Driveway, patio, drainage & more
                              </span>
                            </div>
                            <ArrowRight className="ml-3 h-4 w-4 text-[#04A05C] dark:text-white" data-id="element-1382" data-mp="3lwq6v08uroiv" />
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <div data-id="element-1383" data-mp="3phi7y6m7c6ox">
                  <button onClick={() => setMobileHelpOpen(!mobileHelpOpen)} className="flex items-center justify-end w-full text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1384" data-mp="3ueif6kusffhm">
                    {t('nav.resources')}
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${mobileHelpOpen ? 'rotate-180' : ''}`} data-id="element-1385" data-mp="3kgufdgibrhsl" />
                  </button>
                  <AnimatePresence data-id="element-1386" data-mp="gzcanhxumqwe">
                    {mobileHelpOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} className="overflow-hidden" data-id="element-1387" data-mp="gyu5twx4f4cv">
                        <div className="pt-4 pr-1 space-y-3" data-id="element-1388" data-mp="zrejw885v4wm">
                          <Link to="/help" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1389" data-mp="387fwqxof0spa">
                            Calculator & Quiz
                          </Link>
                          <Link to="/compare" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1390" data-mp="3qwm8g0qskk1l">
                            Compare Materials
                          </Link>
                          <Link to="/projects/new-gravel-driveway" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1391" data-mp="djsu1y9rlc4i">
                            Project Guides
                          </Link>
                          <Link to="/delivery-prep" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1392" data-mp="q0223hcsjban">
                            Delivery Prep Guide
                          </Link>
                          <Link to="/blog" onClick={() => {
                      setIsOpen(false);
                      setMobileHelpOpen(false);
                      collapseCard();
                    }} className="block text-base text-stone-600 dark:text-stone-200 hover:text-brand-orange transition-colors text-right" data-id="element-1393" data-mp="18u7abh68gsrq">
                            Blog
                          </Link>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                {/* Mobile About Menu - Collapsible */}
                {/* About — one destination, so no accordion. */}
                <Link to="/about" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1394" data-mp="12zjb1ihednq1">
                  About
                </Link>

                <Link to="/contact" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1395" data-mp="1qpdn3xtw7898">
                  Contact
                </Link>
                <Link to="/delivery-zones" onClick={() => {
              setIsOpen(false);
              collapseCard();
            }} className="text-stone-900 dark:text-white hover:text-brand-orange font-medium transition-colors text-lg" data-id="element-1396" data-mp="3siv25mkcrbe0">
                  Delivery Zones
                </Link>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </nav>;
}